"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FederalOnlyRules = exports.FederalRules = exports.FEDERAL_READABLE_GENERATOR_STATUSES = void 0;
exports.getFederalAccumulationThresholds = getFederalAccumulationThresholds;
const wasteAccumulation_1 = require("encamp-shared/dist/constants/wasteAccumulation");
const wasteGeneratorStatusThresholds_1 = require("encamp-shared/dist/constants/wasteGeneratorStatusThresholds");
const unwrap_1 = require("../../unwrap");
const onSiteAccumulationThresholds_1 = require("../onSiteAccumulationThresholds");
const _base_1 = require("./_base");
/**
 * The federal generator statuses as the threshold result names them. A state
 * whose status IS the federal one reuses these; a state with a status name of
 * its own (Connecticut's CESQG) authors its own entry beside them.
 */
exports.FEDERAL_READABLE_GENERATOR_STATUSES = {
    LQG: { abbreviation: "LQG", name: "Large Quantity Generator" },
    SQG: { abbreviation: "SQG", name: "Small Quantity Generator" },
    VSQG: { abbreviation: "VSQG", name: "Very Small Quantity Generator" },
};
/**
 * Federal on-site quantity caps (40 CFR 262.14/262.16): VSQGs are capped per
 * classification (1,000 kg non-acute AND 1 kg acute), SQGs by a single
 * 6,000 kg total, and LQGs not at all (time-limited instead).
 */
function getFederalAccumulationThresholds(generatorStatus) {
    switch (generatorStatus) {
        case "VSQG":
            return {
                totalKg: null,
                nonAcuteKg: wasteGeneratorStatusThresholds_1.VSQG_ACCUMULATION_THRESHOLD_KG,
                acuteKg: wasteGeneratorStatusThresholds_1.VSQG_ACCUMULATION_THRESHOLD_ACUTE_KG,
            };
        case "SQG":
            return { totalKg: wasteGeneratorStatusThresholds_1.SQG_ACCUMULATION_THRESHOLD_KG, nonAcuteKg: null, acuteKg: null };
        // LQGs have no accumulation threshold; an unresolvable status gets none either —
        // the caller prompts for setup rather than assuming a status.
        default:
            return onSiteAccumulationThresholds_1.NO_ACCUMULATION_THRESHOLDS;
    }
}
/**
 * FEDERAL ACCUMULATION THRESHOLD EXPECTATIONS — 40 CFR 262.14, 262.16
 *
 * Literal numbers, for the same reason as the area table: this is what pins
 * the constants. Federal law reads no state generator status, so every row spans it.
 */
const FEDERAL_ACCUMULATION_THRESHOLD_EXPECTATIONS = [
    {
        when: { generatorStatus: "LQG" },
        expect: {
            thresholds: { totalKg: null, nonAcuteKg: null, acuteKg: null },
            judgedAgainst: { abbreviation: "LQG", name: "Large Quantity Generator" },
        },
        note: "An LQG is time-limited (262.17); no accumulation threshold applies.",
    },
    {
        when: { generatorStatus: "SQG" },
        expect: {
            thresholds: { totalKg: 6000, nonAcuteKg: null, acuteKg: null },
            judgedAgainst: { abbreviation: "SQG", name: "Small Quantity Generator" },
        },
        note: "262.16(b)(1): a single 6,000 kg total across acute and non-acute waste.",
    },
    {
        when: { generatorStatus: "VSQG" },
        expect: {
            thresholds: { totalKg: null, nonAcuteKg: 1000, acuteKg: 1 },
            judgedAgainst: { abbreviation: "VSQG", name: "Very Small Quantity Generator" },
        },
        note: (0, unwrap_1.unwrap) `262.14(a)(3)-(4). KNOWN GAP (ENG-17590): 100 kg threshold for soil/water/debris
    contaminated by acute haz waste spill is not currently modeled.`,
    },
    {
        when: { generatorStatus: null },
        expect: { thresholds: { totalKg: null, nonAcuteKg: null, acuteKg: null }, judgedAgainst: null },
        note: "No status, no thresholds: the caller prompts for setup rather than assuming a status.",
    },
];
/**
 * FEDERAL EXPECTATIONS
 *
 * Numbers and citations are literal rather than imported: this table is what
 * pins the implementation's constants, so a change to one must fail here.
 */
const FEDERAL_SATELLITE_CAPS = {
    kind: "VOLUME",
    nonAcuteGal: 55,
    acuteGal: 0.25,
    acuteKg: 1,
    moveWindowDays: 3,
    citation: "40 CFR 262.15",
    jurisdiction: "FEDERAL",
};
/**
 * 262.15 imposes no standing satellite clock — the only time limit is the
 * move window once a cap is breached, carried as moveWindowDays on the caps.
 */
const NO_STANDING_SATELLITE_CLOCK = {
    kind: "NO_LIMIT",
    citation: "40 CFR 262.15",
    jurisdiction: "FEDERAL",
};
const FEDERAL_DERIVATIONS = {
    effectiveWasteKind: {
        from: ["wasteKind", "managedAsUniversalWaste", "operatesUnderSubpartP"],
        label: "Federal Effective Subclassification",
        citation: "40 CFR 273.9, 261.6(a)(4), 266.501",
        description: (0, unwrap_1.unwrap) `Which management program governs a container. The UNIVERSAL_WASTE value 
      coming in says only that this could be UW somewhere. A category the state never adopted is not universal
      waste there: part 273's standards apply only "while managed as UW" (40 CFR 273.9,
      262.13(c)(6)). Whether the relevant state program treats it as UW is taken as a second input here. 
      
      Used oil is governed by part 279.
      
      Pharmaceutical waste is treated specially only at facilities that operate under Subpart P,
      which like UW is taken as an additional input. Unlike UW, Subpart P varies by facility, not
      only by state. A facility not operating under it manages pharmaceutical waste under
      the ordinary generator standards.`,
        cases: [
            {
                when: { wasteKind: "UNIVERSAL_WASTE", managedAsUniversalWaste: true },
                produces: "UNIVERSAL_WASTE",
            },
            {
                when: { wasteKind: "UNIVERSAL_WASTE", managedAsUniversalWaste: false },
                produces: "RCRA",
            },
            {
                when: { wasteKind: "USED_OIL" },
                produces: "USED_OIL",
            },
            {
                when: { wasteKind: "PHARMACEUTICAL", operatesUnderSubpartP: true },
                produces: "PHARMACEUTICAL",
            },
            {
                when: { wasteKind: "PHARMACEUTICAL", operatesUnderSubpartP: false },
                produces: "RCRA",
            },
            {
                when: { wasteKind: "RCRA" },
                produces: "RCRA",
            },
            {
                when: { wasteKind: null },
                produces: "RCRA",
            },
        ],
    },
};
const CREDITABLE_PHARMA_NOTE = (0, unwrap_1.unwrap) `
      266.502(f)(1) gives a one-year limit for non-creditable
      pharmaceuticals. Creditable pharmaceuticals have no clock,
      but we don't currently distinguish creditable from non-creditable. 
      We assume the stricter reading.`;
const FEDERAL_EXPECTATIONS = [
    // ── CENTRAL ACCUMULATION ──────────────────────────────────────────────
    // Waste handled by ordinary rules
    // VSQG — 40 CFR 262.14
    {
        given: { effectiveWasteKind: "RCRA" },
        when: {
            areaType: "CENTRAL",
            generatorStatus: "VSQG",
        },
        expect: {
            clock: { kind: "NO_LIMIT", citation: "40 CFR 262.14", jurisdiction: "FEDERAL" },
        },
    },
    // SQG — 40 CFR 262.16
    {
        given: { effectiveWasteKind: "RCRA" },
        when: {
            areaType: "CENTRAL",
            generatorStatus: "SQG",
            mustTransport200Miles: false,
        },
        expect: {
            clock: { kind: "TIME", days: 180, citation: "40 CFR 262.16(b)", jurisdiction: "FEDERAL" },
        },
    },
    {
        given: { effectiveWasteKind: "RCRA" },
        when: {
            areaType: "CENTRAL",
            generatorStatus: "SQG",
            mustTransport200Miles: true,
        },
        expect: {
            clock: { kind: "TIME", days: 270, citation: "40 CFR 262.16(c)", jurisdiction: "FEDERAL" },
        },
    },
    // LQG — 40 CFR 262.17
    {
        given: { effectiveWasteKind: "RCRA" },
        when: {
            areaType: "CENTRAL",
            generatorStatus: "LQG",
        },
        expect: {
            clock: { kind: "TIME", days: 90, citation: "40 CFR 262.17(a)", jurisdiction: "FEDERAL" },
        },
    },
    // Unknown status
    {
        given: { effectiveWasteKind: "RCRA" },
        when: {
            areaType: "CENTRAL",
            generatorStatus: null,
        },
        expect: { clock: { kind: "UNDETERMINED" } },
    },
    // ── SATELLITE ACCUMULATION — 40 CFR 262.15 ────────────────────────────
    {
        given: { effectiveWasteKind: "RCRA" },
        when: {
            areaType: "SATELLITE",
            generatorStatus: "SQG",
        },
        expect: { caps: FEDERAL_SATELLITE_CAPS, clock: NO_STANDING_SATELLITE_CLOCK },
    },
    {
        given: { effectiveWasteKind: "RCRA" },
        when: {
            areaType: "SATELLITE",
            generatorStatus: "LQG",
        },
        expect: { caps: FEDERAL_SATELLITE_CAPS, clock: NO_STANDING_SATELLITE_CLOCK },
    },
    {
        given: { effectiveWasteKind: "RCRA" },
        when: {
            areaType: "SATELLITE",
            generatorStatus: "VSQG",
        },
        expect: {
            caps: { kind: "NO_LIMIT", citation: "40 CFR 262.14", jurisdiction: "FEDERAL" },
            clock: NO_STANDING_SATELLITE_CLOCK,
        },
    },
    {
        given: { effectiveWasteKind: "RCRA" },
        when: {
            areaType: "SATELLITE",
            generatorStatus: null,
        },
        expect: { caps: FEDERAL_SATELLITE_CAPS, clock: NO_STANDING_SATELLITE_CLOCK },
        note: "Take the stricter reading until we know the generator status.",
    },
    // ── KIND-SPECIFIC PROGRAMS ─────────────────────────────────────────────
    // Universal waste, used oil, and Subpart P pharmaceutical containers are
    // governed by their own part of 40 CFR rather than part 262's
    // generator-status-based clocks.
    {
        given: { effectiveWasteKind: "UNIVERSAL_WASTE" },
        when: {
            areaType: "CENTRAL",
        },
        expect: {
            clock: {
                kind: "TIME",
                days: 365,
                citation: "40 CFR 273.15(a), 273.35(a)",
                jurisdiction: "FEDERAL",
            },
        },
        note: (0, unwrap_1.unwrap) `
      273.15(a) (small-quantity handler) and 273.35(a) (large-quantity handler) are word for
      word identical one-year limits. We don't distinguish handler categories, so cite both.`,
    },
    {
        given: { effectiveWasteKind: "UNIVERSAL_WASTE" },
        when: {
            areaType: "SATELLITE",
        },
        expect: {
            caps: { kind: "NO_LIMIT", citation: "40 CFR 261.9, 273.1(b)", jurisdiction: "FEDERAL" },
            clock: {
                kind: "TIME",
                days: 365,
                citation: "40 CFR 273.15(a), 273.35(a)",
                jurisdiction: "FEDERAL",
            },
        },
    },
    {
        given: { effectiveWasteKind: "USED_OIL" },
        when: {
            areaType: "CENTRAL",
        },
        expect: {
            clock: { kind: "NO_LIMIT", citation: "40 CFR 279.22", jurisdiction: "FEDERAL" },
        },
        note: (0, unwrap_1.unwrap) `35-day cap for transfer facilities (not modeled in our system), none for generators.`,
    },
    {
        given: { effectiveWasteKind: "USED_OIL" },
        when: {
            areaType: "SATELLITE",
        },
        expect: {
            caps: { kind: "NO_LIMIT", citation: "40 CFR 261.6(a)(4)", jurisdiction: "FEDERAL" },
            clock: { kind: "NO_LIMIT", citation: "40 CFR 279.22", jurisdiction: "FEDERAL" },
        },
    },
    // Subpart P is facility-scoped. A non-participating
    // facility's pharmaceutical containers land on the RCRA rows above.
    {
        given: { effectiveWasteKind: "PHARMACEUTICAL" },
        when: {
            areaType: "CENTRAL",
        },
        expect: {
            clock: { kind: "TIME", days: 365, citation: "40 CFR 266.502(f)(1)", jurisdiction: "FEDERAL" },
        },
        note: CREDITABLE_PHARMA_NOTE,
    },
    {
        given: { effectiveWasteKind: "PHARMACEUTICAL" },
        when: {
            areaType: "SATELLITE",
        },
        expect: {
            caps: { kind: "NO_LIMIT", citation: "40 CFR 266.501(d)", jurisdiction: "FEDERAL" },
            clock: { kind: "TIME", days: 365, citation: "40 CFR 266.502(f)(1)", jurisdiction: "FEDERAL" },
        },
        note: CREDITABLE_PHARMA_NOTE,
    },
];
const FEDERAL_SOURCES = [
    {
        title: "40 CFR part 261",
        url: "https://www.ecfr.gov/current/title-40/chapter-I/subchapter-I/part-261?toc=1",
    },
    {
        title: "40 CFR part 262",
        url: "https://www.ecfr.gov/current/title-40/chapter-I/subchapter-I/part-262?toc=1",
    },
    {
        title: "40 CFR part 266",
        url: "https://www.ecfr.gov/current/title-40/chapter-I/subchapter-I/part-266?toc=1",
    },
    {
        title: "40 CFR part 273",
        url: "https://www.ecfr.gov/current/title-40/chapter-I/subchapter-I/part-273?toc=1",
    },
    {
        title: "40 CFR part 279",
        url: "https://www.ecfr.gov/current/title-40/chapter-I/subchapter-I/part-279?toc=1",
    },
];
/**
 * Federal RCRA generator accumulation limits, 40 CFR part 262 — the behavior
 * every jurisdiction class starts from. Carries no spec of its own: the
 * federal tables belong to `FederalOnlyRules`, and each state authors its own.
 *
 * Every result stamps `jurisdiction` as a LITERAL matching the citation
 * beside it. Deliberately no `this.jurisdiction` member to DRY that up: an
 * inherited or `super.`-delegated method keeps the subclass's `this`, so a
 * federal answer reached through a state class would come back stamped with
 * the state's jurisdiction next to a CFR citation. Federal methods stamping
 * FEDERAL regardless of the instance is what keeps a state's inherited slots
 * honestly labeled.
 */
class FederalRules extends _base_1.JurisdictionRules {
    /**
     * Which program governs a container here. A state that never adopted a
     * program overrides this, and the federal slots below then route by the
     * state's reading through `this`.
     */
    effectiveWasteKind(ctx) {
        switch (ctx.wasteKind) {
            case "UNIVERSAL_WASTE":
                return ctx.managedAsUniversalWaste ? "UNIVERSAL_WASTE" : "RCRA";
            case "USED_OIL":
                return "USED_OIL";
            case "PHARMACEUTICAL":
                return ctx.operatesUnderSubpartP ? "PHARMACEUTICAL" : "RCRA";
            case "RCRA":
            case null:
                return "RCRA";
            default:
                ((typecheckingHack) => {
                    throw new Error(`Unexpected wasteKind: ${typecheckingHack}`);
                })(ctx.wasteKind);
        }
    }
    accumulationThresholds(ctx) {
        return {
            thresholds: getFederalAccumulationThresholds(ctx.generatorStatus),
            judgedAgainst: ctx.generatorStatus === null
                ? null
                : exports.FEDERAL_READABLE_GENERATOR_STATUSES[ctx.generatorStatus],
        };
    }
    centralClock(ctx) {
        switch (this.effectiveWasteKind(ctx)) {
            case "UNIVERSAL_WASTE":
                /** Universal waste (273.15(a), 273.35(a)) is scoped out of part 262 entirely
                 *
                 * 273.15(a) is the small-quantity handler limit, 273.35(a) the
                 * large-quantity handler limit — word for word identical. This engine
                 * doesn't model handler category (set by UW quantity on hand, not
                 * generator status), hence citing both together.
                 */
                return {
                    kind: "TIME",
                    days: 365,
                    citation: "40 CFR 273.15(a), 273.35(a)",
                    jurisdiction: "FEDERAL",
                };
            case "PHARMACEUTICAL":
                /**
                 * 266.501(d) puts Subpart P "in lieu of parts 262 through 265" ONLY at a facility that
                 * operates under it (a recorded healthcare-facility activity, no
                 * withdrawal, in an adopting state).
                 *
                 * The healthcare-facility limit for non-creditable pharmaceuticals.
                 * Creditable pharmaceuticals have no facility accumulation clock at
                 * all, but this engine's wasteKind doesn't distinguish creditable from
                 * non-creditable, so applying one year to every PHARMACEUTICAL
                 * container is over-strict but never under-strict.
                 */
                return {
                    kind: "TIME",
                    days: 365,
                    citation: "40 CFR 266.502(f)(1)",
                    jurisdiction: "FEDERAL",
                };
            case "USED_OIL":
                // Part 279 sets no generator storage time limit — only a 35-day cap
                // for transfer facilities, which doesn't apply here.
                return { kind: "NO_LIMIT", citation: "40 CFR 279.22", jurisdiction: "FEDERAL" };
            case "RCRA":
                break;
        }
        if (ctx.generatorStatus === "LQG") {
            return { kind: "TIME", days: 90, citation: "40 CFR 262.17(a)", jurisdiction: "FEDERAL" };
        }
        if (ctx.generatorStatus === "SQG") {
            if (ctx.mustTransport200Miles) {
                return { kind: "TIME", days: 270, citation: "40 CFR 262.16(c)", jurisdiction: "FEDERAL" };
            }
            return { kind: "TIME", days: 180, citation: "40 CFR 262.16(b)", jurisdiction: "FEDERAL" };
        }
        if (ctx.generatorStatus === "VSQG") {
            // A VSQG under 262.14 is not subject to 262.16/262.17's central time
            // limits, and 262.14 imposes none of its own; what constrains a VSQG is
            // the facility-wide on-site cap, not a container-specific limit minted on
            // all VSQG containers.
            return { kind: "NO_LIMIT", citation: "40 CFR 262.14", jurisdiction: "FEDERAL" };
        }
        return {
            kind: "UNDETERMINED",
            missingAxes: ["generatorStatus"],
            reason: (0, unwrap_1.unwrap) `
        Storage deadlines need this facility's generator status, which isn't known yet.
        Generator status comes from the facility's EPA registration or its shipment history.`,
        };
    }
    satelliteCaps(ctx) {
        // Universal waste, used oil and Subpart P pharmaceuticals are scoped out
        // of part 262 entirely, so the SAA volume caps and move window never apply
        // regardless of generator status.
        switch (this.effectiveWasteKind(ctx)) {
            case "UNIVERSAL_WASTE":
                return { kind: "NO_LIMIT", citation: "40 CFR 261.9, 273.1(b)", jurisdiction: "FEDERAL" };
            case "USED_OIL":
                return { kind: "NO_LIMIT", citation: "40 CFR 261.6(a)(4)", jurisdiction: "FEDERAL" };
            case "PHARMACEUTICAL":
                return { kind: "NO_LIMIT", citation: "40 CFR 266.501(d)", jurisdiction: "FEDERAL" };
            case "RCRA":
                break;
        }
        if (ctx.generatorStatus === "VSQG") {
            return { kind: "NO_LIMIT", citation: "40 CFR 262.14", jurisdiction: "FEDERAL" };
        }
        // An unknown status still gets the caps — the more protective default.
        return {
            kind: "VOLUME",
            nonAcuteGal: wasteAccumulation_1.SAA_NON_ACUTE_VOLUME_LIMIT_GAL,
            acuteGal: wasteAccumulation_1.SAA_ACUTE_VOLUME_LIMIT_GAL,
            acuteKg: wasteAccumulation_1.SAA_ACUTE_MASS_LIMIT_KG,
            moveWindowDays: wasteAccumulation_1.SATELLITE_MOVE_WINDOW_DAYS,
            citation: "40 CFR 262.15",
            jurisdiction: "FEDERAL",
        };
    }
    satelliteClock(ctx) {
        switch (this.effectiveWasteKind(ctx)) {
            case "UNIVERSAL_WASTE":
                return {
                    kind: "TIME",
                    days: 365,
                    citation: "40 CFR 273.15(a), 273.35(a)",
                    jurisdiction: "FEDERAL",
                };
            case "PHARMACEUTICAL":
                return {
                    kind: "TIME",
                    days: 365,
                    citation: "40 CFR 266.502(f)(1)",
                    jurisdiction: "FEDERAL",
                };
            case "USED_OIL":
                return { kind: "NO_LIMIT", citation: "40 CFR 279.22", jurisdiction: "FEDERAL" };
            case "RCRA":
                // 262.15 sets no standing clock on satellite accumulation for ordinary waste — the only
                // time limit is the exceedance-triggered move window, carried on the caps.
                return { kind: "NO_LIMIT", citation: "40 CFR 262.15", jurisdiction: "FEDERAL" };
        }
    }
}
exports.FederalRules = FederalRules;
/**
 * The FEDERAL registry entry, and the answer for any state without a class
 * of its own. Exists apart from `FederalRules` because that class reads a
 * generic context — that is what lets a state inherit — and a generic class
 * cannot say how to read a context whose type is still a parameter.
 */
class FederalOnlyRules extends FederalRules {
    constructor() {
        super(...arguments);
        this.expectations = FEDERAL_EXPECTATIONS;
        this.accumulationThresholdExpectations = FEDERAL_ACCUMULATION_THRESHOLD_EXPECTATIONS;
        this.derivations = FEDERAL_DERIVATIONS;
        this.sources = FEDERAL_SOURCES;
        // Federal law recognizes no state tier, so it refuses none either.
        this.stateStatusDomain = [];
    }
    narrowToJurisdiction(ctx) {
        return ctx;
    }
    narrowAccumulationThresholdContext(ctx) {
        return { generatorStatus: ctx.generatorStatus };
    }
}
exports.FederalOnlyRules = FederalOnlyRules;
//# sourceMappingURL=_FEDERAL.js.map